import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Search, Star, StarOff, Sparkles, Upload, X, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcupunctureCardManager() {
  const [cards, setCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({
    point_name: '',
    channel_number: '',
    fun_fact: '',
    actions: '',
    indications: '',
    location: '',
    needling_technique: '',
    common_combinations: '',
    commentary: '',
    spirit_of_point: '',
    image_url: '',
    is_featured: false
  });
  const [aiFieldContext, setAiFieldContext] = useState('');
  const [aiFieldName, setAiFieldName] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadCards();
  }, [searchTerm, sortBy]);

  const loadCards = async () => {
    try {
      const params = { sort_by: sortBy };
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${API}/admin/acupuncture-cards`, {
        params,
        withCredentials: true
      });
      setCards(response.data);
    } catch (error) {
      toast.error('Failed to load cards');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCard) {
        await axios.put(`${API}/admin/acupuncture-cards/${editingCard.id}`, {
          ...formData,
          id: editingCard.id,
          created_at: editingCard.created_at
        }, { withCredentials: true });
        toast.success('Card updated successfully!');
      } else {
        await axios.post(`${API}/admin/acupuncture-cards`, formData, {
          withCredentials: true
        });
        toast.success('Card created successfully!');
      }
      
      resetForm();
      loadCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save card');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    
    try {
      await axios.delete(`${API}/admin/acupuncture-cards/${id}`, {
        withCredentials: true
      });
      toast.success('Card deleted successfully');
      loadCards();
    } catch (error) {
      toast.error('Failed to delete card');
    }
  };

  const toggleFeature = async (id) => {
    try {
      await axios.post(`${API}/admin/acupuncture-cards/${id}/feature`, {}, {
        withCredentials: true
      });
      toast.success('Featured status updated');
      loadCards();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleAiFillField = async (fieldName) => {
    if (!aiFieldContext.trim() && !imageFile) {
      toast.error('Please provide context or upload an image for AI to work with');
      return;
    }
    
    setAiGenerating(true);
    setAiFieldName(fieldName);
    
    try {
      const response = await axios.post(`${API}/admin/acupuncture-cards/ai-fill-field`, {
        field_name: fieldName,
        context: aiFieldContext,
        is_image: false
      }, { withCredentials: true });
      
      setFormData({ ...formData, [fieldName]: response.data.value });
      toast.success(`AI filled ${fieldName}!`);
      setAiFieldContext('');
    } catch (error) {
      toast.error('AI generation failed');
    } finally {
      setAiGenerating(false);
      setAiFieldName('');
    }
  };

  const handleAiFillAllFields = async () => {
    if (!aiFieldContext.trim() && !imageFile) {
      toast.error('Please provide context or upload an image for AI to work with');
      return;
    }
    
    setAiGenerating(true);
    setAiFieldName('all');
    
    try {
      const formDataToSend = new FormData();
      if (imageFile) {
        formDataToSend.append('file', imageFile);
      }
      formDataToSend.append('context', aiFieldContext);
      
      const response = await axios.post(`${API}/admin/acupuncture-cards/ai-fill-all-fields`, formDataToSend, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({ ...formData, ...response.data.card_data });
      toast.success('AI filled all fields!');
      setAiFieldContext('');
      setImageFile(null);
      setImagePreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiGenerating(false);
      setAiFieldName('');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const resetForm = () => {
    setFormData({
      point_name: '',
      channel_number: '',
      fun_fact: '',
      actions: '',
      indications: '',
      location: '',
      needling_technique: '',
      common_combinations: '',
      commentary: '',
      spirit_of_point: '',
      image_url: '',
      is_featured: false
    });
    setEditingCard(null);
    setShowForm(false);
  };

  const startEdit = (card) => {
    setFormData(card);
    setEditingCard(card);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title text-[#2C1810]">Acupuncture Cards</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="retro-button flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create New Card
        </button>
      </div>

      {/* Search and Sort */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-[#6B4423]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by point name or channel..."
            className="w-full pl-10 pr-4 py-2 border-2 border-[#6B4423] rounded-lg"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 border-2 border-[#6B4423] rounded-lg"
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="name_asc">Name A-Z</option>
          <option value="name_desc">Name Z-A</option>
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <div className="cassette-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl retro-title text-[#2C1810]">
              {editingCard ? 'Edit Card' : 'Create New Card'}
            </h3>
            <button onClick={resetForm} className="p-2 hover:bg-[#E8B67A] rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* AI Context Helper */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
            <label className="block text-sm font-bold text-purple-900 mb-2">
              <Sparkles className="w-4 h-4 inline mr-2" />
              AI Field Assistant
            </label>
            
            {/* Image Upload */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Upload Image for AI to Read:
              </label>
              <div className="flex gap-2 items-start">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="flex-1 text-sm text-gray-800"
                />
                {imagePreview && (
                  <button
                    type="button"
                    onClick={clearImage}
                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {imagePreview && (
                <img src={imagePreview} alt="Preview" className="mt-2 max-w-xs rounded border-2 border-purple-300" />
              )}
            </div>

            <label className="block text-xs font-semibold text-gray-800 mb-1">
              OR Paste/Type Context:
            </label>
            <textarea
              value={aiFieldContext}
              onChange={(e) => setAiFieldContext(e.target.value)}
              placeholder="Paste text or describe the point..."
              className="w-full p-3 border-2 border-purple-300 rounded-lg mb-3 text-gray-900"
              rows="3"
            />
            
            {/* Fill All Button */}
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={handleAiFillAllFields}
                disabled={aiGenerating}
                className="retro-button bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {aiGenerating && aiFieldName === 'all' ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Fill ALL Fields with AI
                  </>
                )}
              </button>
              <span className="text-xs text-purple-700">
                Or click sparkle buttons below to fill individual fields
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Bold/Important Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#2C1810] mb-1">
                  Point Name * (Bold)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.point_name}
                    onChange={(e) => setFormData({ ...formData, point_name: e.target.value })}
                    className="flex-1 p-2 border-2 border-[#6B4423] rounded-lg"
                    placeholder="e.g., Hegu"
                  />
                  <button
                    type="button"
                    onClick={() => handleAiFillField('point_name')}
                    disabled={aiGenerating}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {aiGenerating && aiFieldName === 'point_name' ? '...' : <Sparkles className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[#2C1810] mb-1">
                  Channel & Number * (Bold)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.channel_number}
                    onChange={(e) => setFormData({ ...formData, channel_number: e.target.value })}
                    className="flex-1 p-2 border-2 border-[#6B4423] rounded-lg"
                    placeholder="e.g., LI 4"
                  />
                  <button
                    type="button"
                    onClick={() => handleAiFillField('channel_number')}
                    disabled={aiGenerating}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {aiGenerating && aiFieldName === 'channel_number' ? '...' : <Sparkles className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">
                Fun Fact * (Bold)
              </label>
              <div className="flex gap-2">
                <textarea
                  required
                  value={formData.fun_fact}
                  onChange={(e) => setFormData({ ...formData, fun_fact: e.target.value })}
                  className="flex-1 p-2 border-2 border-[#6B4423] rounded-lg"
                  rows="2"
                  placeholder="An exciting, memorable statement about this point"
                />
                <button
                  type="button"
                  onClick={() => handleAiFillField('fun_fact')}
                  disabled={aiGenerating}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {aiGenerating && aiFieldName === 'fun_fact' ? '...' : <Sparkles className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Regular Fields */}
            {[
              { name: 'actions', label: 'Actions', placeholder: 'Therapeutic actions and functions' },
              { name: 'indications', label: 'Indications', placeholder: 'Conditions and symptoms treated' },
              { name: 'location', label: 'Location', placeholder: 'Precise anatomical location' },
              { name: 'needling_technique', label: 'Needling Technique', placeholder: 'Depth, angle, technique' },
              { name: 'common_combinations', label: 'Common Combinations', placeholder: 'Points used together' },
              { name: 'commentary', label: 'Commentary', placeholder: 'Additional insights' }
            ].map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-bold text-[#2C1810] mb-1">
                  {field.label} *
                </label>
                <div className="flex gap-2">
                  <textarea
                    required
                    value={formData[field.name]}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="flex-1 p-2 border-2 border-[#6B4423] rounded-lg"
                    rows="3"
                    placeholder={field.placeholder}
                  />
                  <button
                    type="button"
                    onClick={() => handleAiFillField(field.name)}
                    disabled={aiGenerating}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {aiGenerating && aiFieldName === field.name ? '...' : <Sparkles className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}

            {/* Optional Field */}
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">
                Spirit of the Point (Optional)
              </label>
              <div className="flex gap-2">
                <textarea
                  value={formData.spirit_of_point}
                  onChange={(e) => setFormData({ ...formData, spirit_of_point: e.target.value })}
                  className="flex-1 p-2 border-2 border-[#6B4423] rounded-lg"
                  rows="2"
                  placeholder="Emotional/spiritual aspects (optional)"
                />
                <button
                  type="button"
                  onClick={() => handleAiFillField('spirit_of_point')}
                  disabled={aiGenerating}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {aiGenerating && aiFieldName === 'spirit_of_point' ? '...' : <Sparkles className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">
                Image URL (Optional)
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full p-2 border-2 border-[#6B4423] rounded-lg"
                placeholder="https://..."
              />
            </div>

            {/* Featured Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="w-5 h-5"
              />
              <label htmlFor="featured" className="font-bold text-[#2C1810]">
                Feature on Main Page
              </label>
            </div>

            <div className="flex gap-3">
              <button type="submit" className="retro-button">
                {editingCard ? 'Update Card' : 'Create Card'}
              </button>
              <button type="button" onClick={resetForm} className="retro-button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Cards List */}
      <div className="space-y-4">
        {cards.map((card) => (
          <div key={card.id} className="cassette-card p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl retro-title text-[#2C1810]">
                  {card.point_name} ({card.channel_number})
                </h3>
                <p className="text-[#6B4423] italic">{card.fun_fact}</p>
                {card.is_featured && (
                  <span className="inline-block mt-2 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold">
                    ⭐ FEATURED
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleFeature(card.id)}
                  className="p-2 hover:bg-yellow-100 rounded"
                  title={card.is_featured ? 'Unfeature' : 'Feature'}
                >
                  {card.is_featured ? <Star className="w-5 h-5 text-yellow-600" /> : <StarOff className="w-5 h-5 text-gray-400" />}
                </button>
                <button
                  onClick={() => startEdit(card)}
                  className="p-2 hover:bg-[#E8B67A] rounded"
                  title="Edit"
                >
                  <Edit className="w-5 h-5 text-[#6B4423]" />
                </button>
                <button
                  onClick={() => handleDelete(card.id)}
                  className="p-2 hover:bg-red-100 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5 text-red-600" />
                </button>
              </div>
            </div>
            <div className="text-sm text-[#6B4423]">
              <p><strong>Location:</strong> {card.location}</p>
              <p><strong>Actions:</strong> {card.actions}</p>
            </div>
          </div>
        ))}
      </div>

      {cards.length === 0 && !showForm && (
        <div className="text-center py-20">
          <p className="text-xl text-[#6B4423]">No acupuncture cards yet. Create your first one!</p>
        </div>
      )}
    </div>
  );
}
